import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const FeaturedCaps = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/api/products`,
          { params: { collection: "Caps" } }
        );
        setProducts(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const displayProducts = products.slice(0, 8);

  return (
    <section className="fcs-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

        .fcs-root {
          background: #f8f8f8;
          padding: 80px 0 72px;
          font-family: 'Exo 2', sans-serif;
        }

        /* ── HEADER ── */
        .fcs-header {
          max-width: 1400px;
          margin: 0 auto 40px;
          padding: 0 48px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
        }

        .fcs-eyebrow {
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.5em;
          text-transform: uppercase;
          color: rgba(0,0,0,0.4);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .fcs-eyebrow::before {
          content: '';
          width: 24px;
          height: 1px;
          background: rgba(0,0,0,0.2);
          display: block;
          flex-shrink: 0;
        }

        .fcs-title {
          font-size: clamp(28px, 4vw, 56px);
          font-weight: 200;
          color: #000;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          line-height: 1;
          margin: 0;
        }
        .fcs-title span { font-weight: 600; }

        .fcs-cta-header {
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: #000;
          text-decoration: none;
          position: relative;
          padding-bottom: 6px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .fcs-cta-header::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 100%;
          height: 1px;
          background: rgba(0,0,0,0.25);
          transition: background 0.25s ease;
        }
        .fcs-cta-header:hover::after { background: #000; }

        /* ── RULE ── */
        .fcs-rule {
          max-width: 1400px;
          margin: 0 auto 36px;
          padding: 0 48px;
        }
        .fcs-rule hr {
          border: none;
          border-top: 1px solid rgba(0,0,0,0.08);
        }

        /* ── GRID ── */
        .fcs-grid {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 48px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2px;
        }

        /* ── SKELETON ── */
        .fcs-skeleton {
          background: #e8e8e8;
          aspect-ratio: 3 / 4;
          position: relative;
          overflow: hidden;
        }
        .fcs-skeleton::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%);
          animation: fcs-shimmer 1.6s infinite;
        }
        @keyframes fcs-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        /* ── CARD ── */
        .fcs-card {
          position: relative;
          background: #f8f8f8;
          overflow: hidden;
        }

        .fcs-card-link {
          display: block;
          text-decoration: none;
        }

        .fcs-img-wrap {
          width: 100%;
          aspect-ratio: 3 / 4;
          overflow: hidden;
        }

        .fcs-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .fcs-card:hover .fcs-img { transform: scale(1.04); }

        /* Caption — no background */
        .fcs-caption {
          padding: 10px 2px 14px;
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
        }

        .fcs-name {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: #000;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .fcs-price {
          font-size: 12px;
          font-weight: 300;
          letter-spacing: 0.1em;
          color: rgba(0,0,0,0.5);
          white-space: nowrap;
          flex-shrink: 0;
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) {
          .fcs-grid { grid-template-columns: repeat(2, 1fr); padding: 0 24px; }
          .fcs-header { padding: 0 24px; }
          .fcs-rule { padding: 0 24px; }
        }

        @media (max-width: 768px) {
          .fcs-root { padding: 52px 0 48px; }
          .fcs-header { flex-direction: row; align-items: flex-end; padding: 0 16px; margin-bottom: 24px; }
          .fcs-title { font-size: clamp(22px, 7vw, 32px); }
          .fcs-eyebrow { font-size: 8px; margin-bottom: 8px; }
          .fcs-rule { padding: 0 16px; margin-bottom: 20px; }
          .fcs-grid { grid-template-columns: repeat(2, 1fr); padding: 0 16px; gap: 8px; }
          .fcs-caption { padding: 8px 0 10px; flex-direction: column; gap: 2px; }
          .fcs-name { font-size: 9px; letter-spacing: 0.15em; }
          .fcs-price { font-size: 11px; }
          .fcs-cta-header { font-size: 8px; letter-spacing: 0.3em; }
        }

        @media (max-width: 360px) {
          .fcs-grid { gap: 6px; padding: 0 12px; }
          .fcs-header { padding: 0 12px; }
        }
      `}</style>

      {/* HEADER */}
      <div className="fcs-header">
        <div>
          <div className="fcs-eyebrow">Featured Category</div>
          <h2 className="fcs-title"> <span>Caps</span></h2>
        </div>
        <Link to="/collections/OverSized TShirt" className="fcs-cta-header">
          Shop All
        </Link>
      </div>

      {/* DIVIDER */}
      <div className="fcs-rule"><hr /></div>

      {/* GRID */}
      <div className="fcs-grid">
        {loading
          ? [...Array(8)].map((_, i) => <div key={i} className="fcs-skeleton" />)
          : displayProducts.map((product) => (
              <div key={product._id} className="fcs-card">
                <Link to={`/product/${product._id}`} className="fcs-card-link">
                  <div className="fcs-img-wrap">
                    <img
                      src={product.images?.[0]?.url}
                      alt={product.name || "Product"}
                      className="fcs-img"
                      draggable="false"
                    />
                  </div>
                  <div className="fcs-caption">
                    <span className="fcs-name">{product.name}</span>
                    <span className="fcs-price">₹{product.price}</span>
                  </div>
                </Link>
              </div>
            ))}
      </div>
    </section>
  );
};

export default FeaturedCaps;
